import { Wrench } from 'lucide-react';
import { palette } from '../../styles/tokens';
import { MetricCard } from '../../components/common/MetricCard';
import { EmptyState } from '../../components/common/EmptyState';
import { SortableHeader } from '../../components/common/SortableHeader';
import { PaginationFooter } from '../../components/common/PaginationFooter';
import { JOB_STATUSES, JOB_PRIORITIES, ACTIVE_STATUSES, STATUS_FLOW, TD } from './config';
import type { JobRow, JobFilters, JobSummary } from './hooks';
import type { SortControls } from '../../hooks/useSortState';
import type { PaginationControls } from '../../hooks/usePagination';

interface TeamUser { id: string; first_name: string; last_name: string; }

interface Props {
  jobs: JobRow[];
  totalCount: number | null;
  users: TeamUser[];
  summary: JobSummary;
  filters: JobFilters;
  setFilters: (f: JobFilters) => void;
  sort: SortControls;
  pagination: PaginationControls;
  loading: boolean;
  onSelect: (job: JobRow) => void;
  onAdvance: (id: string, nextStatus: string) => void;
  onNewJob?: () => void;
}

function fmt$(n: number): string { return `$${n.toLocaleString('en-CA', { minimumFractionDigits: 0 })}`; }

export function ActiveTab({ jobs, totalCount, users, summary, filters, setFilters, sort, pagination, loading, onSelect, onAdvance, onNewJob }: Props) {
  const todayStr = new Date().toISOString().split('T')[0];

  return (
    <div>
      {/* Summary */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <MetricCard label="Active jobs" value={String(summary.activeCount)} />
        <MetricCard label="Completed this week" value={String(summary.thisWeek)} />
        <MetricCard label="Overdue" value={String(summary.overdueCount)}
          delta={summary.overdueCount > 0 ? 'Needs attention' : undefined}
          deltaType={summary.overdueCount > 0 ? 'warn' : undefined} />
        <MetricCard label="Revenue this month" value={fmt$(summary.revenueThisMonth)} />
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={filters.search} onChange={e => setFilters({ ...filters, search: e.target.value })}
          placeholder="Search jobs..." style={{ padding: '7px 12px', borderRadius: 6, border: `1px solid ${palette.g200}`, fontSize: 13, fontFamily: 'inherit', outline: 'none', width: 200 }} />
        <select value={filters.status ?? ''} onChange={e => setFilters({ ...filters, status: e.target.value || null })}
          style={{ padding: '7px 10px', borderRadius: 6, border: `1px solid ${palette.g200}`, fontSize: 13, fontFamily: 'inherit', color: palette.g700 }}>
          <option value="">All Statuses</option>
          {ACTIVE_STATUSES.map(s => <option key={s} value={s}>{JOB_STATUSES[s]?.label ?? s}</option>)}
        </select>
        <select value={filters.priority ?? ''} onChange={e => setFilters({ ...filters, priority: e.target.value || null })}
          style={{ padding: '7px 10px', borderRadius: 6, border: `1px solid ${palette.g200}`, fontSize: 13, fontFamily: 'inherit', color: palette.g700 }}>
          <option value="">All Priorities</option>
          {Object.entries(JOB_PRIORITIES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={filters.assignedId ?? ''} onChange={e => setFilters({ ...filters, assignedId: e.target.value || null })}
          style={{ padding: '7px 10px', borderRadius: 6, border: `1px solid ${palette.g200}`, fontSize: 13, fontFamily: 'inherit', color: palette.g700 }}>
          <option value="">All Team</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
        </select>
      </div>

      {/* Table */}
      {jobs.length === 0 && !loading ? (
        <EmptyState icon={<Wrench size={22} />} title="No active jobs" subtitle="Create a job to get started." actionLabel="New Job" onAction={onNewJob} />
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <SortableHeader column="title" label="Title" sort={sort} />
            <th style={{ fontSize: 11, fontWeight: 600, color: palette.g400, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 12px', textAlign: 'left', borderBottom: `1px solid ${palette.g150}`, whiteSpace: 'nowrap' }}>Client</th>
            <SortableHeader column="priority" label="Priority" sort={sort} />
            <SortableHeader column="status" label="Status" sort={sort} />
            <th style={{ fontSize: 11, fontWeight: 600, color: palette.g400, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 12px', textAlign: 'left', borderBottom: `1px solid ${palette.g150}`, whiteSpace: 'nowrap' }}>Assigned</th>
            <SortableHeader column="scheduled_date" label="Scheduled" sort={sort} />
            <SortableHeader column="total_amount" label="Amount" sort={sort} align="right" />
            <th style={{ fontSize: 11, fontWeight: 600, color: palette.g400, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 12px', textAlign: 'left', borderBottom: `1px solid ${palette.g150}`, whiteSpace: 'nowrap' }}>Action</th>
          </tr></thead>
          <tbody>
            {jobs.map(j => {
              const st = JOB_STATUSES[j.status] ?? JOB_STATUSES.new;
              const pr = JOB_PRIORITIES[j.priority ?? 'medium'] ?? JOB_PRIORITIES.medium;
              const overdue = ACTIVE_STATUSES.includes(j.status) && j.scheduled_date && j.scheduled_date < todayStr;
              const nextIdx = STATUS_FLOW.indexOf(j.status) + 1;
              const nextStatus = nextIdx < STATUS_FLOW.length ? STATUS_FLOW[nextIdx] : null;
              return (
                <tr key={j.id} onClick={() => onSelect(j)} style={{ cursor: 'pointer', transition: 'background 150ms ease' }}
                  onMouseEnter={e => (e.currentTarget.style.background = palette.g50)}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                  <td style={{ ...TD, fontWeight: 600, color: palette.g800 }}>{j.title}</td>
                  <td style={TD}>{j.client_name ?? '—'}</td>
                  <td style={TD}><span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500, background: pr.bg, color: pr.fg }}>{pr.label}</span></td>
                  <td style={TD}><span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500, background: st.bg, color: st.fg }}>{st.label}</span></td>
                  <td style={TD}>{j.assigned_name ?? '—'}</td>
                  <td style={{ ...TD, color: overdue ? palette.red : palette.g700, fontWeight: overdue ? 600 : 400 }}>
                    {j.scheduled_date ?? '—'}
                  </td>
                  <td style={{ ...TD, textAlign: 'right' }}>{j.total_amount ? fmt$(j.total_amount) : '—'}</td>
                  <td style={TD}>
                    {nextStatus && (
                      <button onClick={e => { e.stopPropagation(); onAdvance(j.id, nextStatus); }} style={{
                        padding: '4px 10px', borderRadius: 4, border: 'none', fontSize: 11, fontWeight: 600,
                        background: palette.accent, color: palette.white, cursor: 'pointer', fontFamily: 'inherit',
                      }}>{JOB_STATUSES[nextStatus]?.label ?? nextStatus}</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <PaginationFooter pagination={pagination} totalCount={totalCount} loading={loading} />
    </div>
  );
}
